import React, { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { 
  Building, 
  Plus, 
  ArrowRight, 
  Users, 
  Settings,
  Crown,
  Zap,
  LogOut,
  Star,
  StarOff,
  MoreVertical,
  ExternalLink
} from "lucide-react";
import { useAuth } from "@/contexts/auth-context";
import { getWorkspaceUrl, getWorkspaceUrlFromObject } from "@/utils/tenant-utils";
import { WorkspaceService, UserWorkspace } from '@/services/workspace-service.ts';
import { ProfileSettingsModal } from "@/components/ProfileSettingsModal";
import { useToast } from "@/hooks/use-toast";
import { Header } from '@/components/Header';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const WorkspaceSelector = () => {
  const { user, signOut } = useAuth();
  const { toast } = useToast();
  const [workspaces, setWorkspaces] = useState<UserWorkspace[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showProfileSettings, setShowProfileSettings] = useState(false);
  const [newWorkspaceName, setNewWorkspaceName] = useState('');
  const [newWorkspaceSlug, setNewWorkspaceSlug] = useState('');
  const [creating, setCreating] = useState(false);
  const [slugError, setSlugError] = useState<string | null>(null);

  useEffect(() => {
    loadUserWorkspaces();
  }, []);

  const loadUserWorkspaces = async () => {
    try {
      setLoading(true);
      console.log('🔄 Loading user workspaces...');
      const userWorkspaces = await WorkspaceService.getUserWorkspaces();
      console.log('✅ Loaded workspaces:', userWorkspaces);
      setWorkspaces(userWorkspaces);
    } catch (error) {
      console.error('❌ Error loading workspaces:', error);
      toast({
        title: "Error",
        description: "Failed to load your workspaces",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const generateSlugFromName = (name: string) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '')
      .slice(0, 20);
  };

  const handleNameChange = (name: string) => {
    setNewWorkspaceName(name);
    if (name) {
      const slug = generateSlugFromName(name);
      setNewWorkspaceSlug(slug);
      validateSlug(slug);
    } else {
      setNewWorkspaceSlug('');
      setSlugError(null);
    }
  };

  const validateSlug = async (slug: string) => {
    if (!slug) {
      setSlugError('Workspace name is required');
      return;
    }

    if (slug.length < 3) {
      setSlugError('Workspace name must be at least 3 characters');
      return;
    }

    if (slug.length > 20) {
      setSlugError('Workspace name must be 20 characters or less');
      return;
    }

    if (!/^[a-z0-9][a-z0-9-]*[a-z0-9]$|^[a-z0-9]$/.test(slug)) {
      setSlugError('Invalid format. Use only letters, numbers, and hyphens');
      return;
    }

    const isAvailable = await WorkspaceService.isSlugAvailable(slug);
    if (!isAvailable) {
      setSlugError('This name is not available. Please choose a different name');
      return;
    }

    setSlugError(null);
  };

  const handleCreateWorkspace = async () => {
    if (!newWorkspaceName || !newWorkspaceSlug || slugError) return;

    try {
      setCreating(true);
      
      const newWorkspace = await WorkspaceService.createWorkspace(newWorkspaceName, newWorkspaceSlug);
      
      if (newWorkspace) {
        toast({
          title: "Success!",
          description: `Workspace "${newWorkspaceName}" created successfully`
        });
        
        // Refresh workspace list
        await loadUserWorkspaces();
        setShowCreateForm(false);
        setNewWorkspaceName('');
        setNewWorkspaceSlug('');
      } else {
        throw new Error('Failed to create workspace');
      }
    } catch (error) {
      console.error('Error creating workspace:', error);
      toast({
        title: "Error",
        description: "Failed to create workspace. Please try again.",
        variant: "destructive"
      });
    } finally {
      setCreating(false);
    }
  };

  const handleSetDefault = async (workspaceId: string) => {
    try {
      const success = await WorkspaceService.setDefaultWorkspace(workspaceId);
      if (success) {
        await loadUserWorkspaces();
        toast({
          title: "Success",
          description: "Default workspace updated"
        });
      } else {
        throw new Error('Failed to set default workspace');
      }
    } catch (error) {
      console.error('Error setting default workspace:', error);
      toast({
        title: "Error",
        description: "Failed to update default workspace",
        variant: "destructive"
      });
    }
  };

  const handleEnterWorkspace = (workspace: UserWorkspace) => {
    const workspaceUrl = getWorkspaceUrlFromObject(workspace);
    window.location.href = workspaceUrl;
  };

  const getRoleBadge = (role: UserWorkspace['role']) => {
    switch (role) {
      case 'workspace_admin':
        return 'Admin';
      case 'workspace_member':
        return 'Member';
      default:
        return 'User';
    }
  };

  // Get the base domain from environment variable
  const APP_DOMAIN = import.meta.env.VITE_APP_DOMAIN || 'wattfactory.tech';

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 animate-spin border-2 border-primary border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading your workspaces...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-secondary/20">
      {/* Header */}
      <Header />

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          {/* Page Header */}
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold mb-2">My Workspaces</h1>
            <p className="text-muted-foreground">
              Select a workspace to continue, or create a new one
            </p>
          </div>

          {/* Create Workspace Form */}
          {showCreateForm && (
            <Card className="mb-6">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Plus className="w-5 h-5" />
                  Create New Workspace
                </CardTitle>
                <CardDescription>
                  Set up a new workspace for your team or project
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="workspaceName">Workspace Name</Label>
                  <Input
                    id="workspaceName"
                    value={newWorkspaceName}
                    onChange={(e) => handleNameChange(e.target.value)}
                    placeholder="My Manufacturing Team"
                    maxLength={50}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="workspaceSlug">Workspace URL</Label>
                  <div className="flex items-center gap-2">
                    <Input
                      id="workspaceSlug"
                      value={newWorkspaceSlug}
                      onChange={(e) => {
                        setNewWorkspaceSlug(e.target.value);
                        validateSlug(e.target.value);
                      }}
                      placeholder="my-manufacturing-team"
                    />
                    <span className="text-sm text-muted-foreground whitespace-nowrap">
                      .{APP_DOMAIN}
                    </span>
                  </div>
                  {slugError && (
                    <p className="text-sm text-destructive">{slugError}</p>
                  )}
                </div>

                <div className="flex gap-2 pt-2">
                  <Button
                    onClick={handleCreateWorkspace}
                    disabled={creating || !newWorkspaceName || !newWorkspaceSlug || !!slugError}
                  >
                    {creating ? 'Creating...' : 'Create Workspace'}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowCreateForm(false);
                      setNewWorkspaceName('');
                      setNewWorkspaceSlug('');
                      setSlugError(null);
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Add Workspace Button */}
          {!showCreateForm && (
            <div className="mb-6">
              <Button
                onClick={() => setShowCreateForm(true)}
                className="gap-2"
              >
                <Plus className="w-4 h-4" />
                Create New Workspace
              </Button>
            </div>
          )}

          {/* Workspaces Grid */}
          {workspaces.length === 0 ? (
            <Card>
              <CardContent className="text-center py-12">
                <Building className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2">No Workspaces Yet</h3>
                <p className="text-muted-foreground mb-4">
                  Create your first workspace to get started with WattFactory
                </p>
                <Button onClick={() => setShowCreateForm(true)} className="gap-2">
                  <Plus className="w-4 h-4" />
                  Create Your First Workspace
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {workspaces.map((workspace) => (
                <Card key={workspace.id} className="hover:shadow-lg transition-shadow cursor-pointer group">
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <CardTitle className="text-lg flex items-center gap-2">
                          <Building className="w-5 h-5" />
                          {workspace.name}
                          {workspace.is_default && (
                            <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                          )}
                        </CardTitle>
                        <CardDescription className="mt-1">
                          {workspace.domain && workspace.domain_verified ? (
                            <span className="flex items-center gap-1">
                              <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                              {workspace.domain}
                            </span>
                          ) : (
                            <span>{workspace.slug}.{APP_DOMAIN}</span>
                          )}
                        </CardDescription>
                      </div>
                      
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" className="opacity-0 group-hover:opacity-100">
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {!workspace.is_default && (
                            <DropdownMenuItem onClick={() => handleSetDefault(workspace.id)}>
                              <Star className="w-4 h-4 mr-2" />
                              Set as Default
                            </DropdownMenuItem>
                          )}
                          {workspace.is_default && (
                            <DropdownMenuItem disabled>
                              <StarOff className="w-4 h-4 mr-2" />
                              Default Workspace
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </CardHeader>
                  
                  <CardContent className="pt-0">
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        {getRoleBadge(workspace.role)}
                        <div className="flex items-center gap-1 text-sm text-muted-foreground">
                          <Users className="w-4 h-4" />
                          {workspace.member_count || 1}
                        </div>
                      </div>
                      
                      <Button
                        onClick={() => handleEnterWorkspace(workspace)}
                        className="w-full gap-2"
                        variant={workspace.is_default ? "default" : "outline"}
                      >
                        Enter Workspace
                        <ArrowRight className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </main>

      {/* Profile Settings Modal */}
      <ProfileSettingsModal
        isOpen={showProfileSettings}
        onClose={() => setShowProfileSettings(false)}
      />
    </div>
  );
};

export default WorkspaceSelector; 